import { BellRing, ChevronLeft, KeyRound, Mail, MonitorSmartphone, Moon, Sun, Trash2, Upload } from "lucide-react";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, NotificationPreference, TokenResponse, User, WebPushConfiguration } from "../api";
import { Avatar, Loading, PageHead, RequirementHint, focusInvalidField, Toggle } from "../components/ui";
import type { PersonalSettingsSection } from "../lib/types";
import {
  applyPreferences, kanbanColors, kanbanPatterns, kanbanStyleClass,
  type KanbanColor, type KanbanPattern, type UserPreferences,
} from "../lib/preferences";
import {
  currentWebPushSubscription, resolveWebPushPublicKey, subscribeCurrentBrowser, supportsWebPush,
  unsubscribeCurrentBrowser,
} from "../lib/webPush";

const notificationKinds: Array<{
  key: "taskAssignedEnabled" | "taskReviewEnabled" | "taskDecisionEnabled" | "taskCommentEnabled";
  titleKey: string;
  descriptionKey: string;
}> = [
  { key: "taskAssignedEnabled", titleKey: "notifications.assigned", descriptionKey: "notifications.assignedHelp" },
  { key: "taskReviewEnabled", titleKey: "notifications.review", descriptionKey: "notifications.reviewHelp" },
  { key: "taskDecisionEnabled", titleKey: "notifications.decision", descriptionKey: "notifications.decisionHelp" },
  { key: "taskCommentEnabled", titleKey: "notifications.comment", descriptionKey: "notifications.commentHelp" },
];

export function PersonalSettingsPage({ email, profile: initialProfile, section, onSection, onChanged, initialPreferences, onPreferencesChanged, onPasswordChanged }: {
  email: string;
  profile?: User;
  section: PersonalSettingsSection;
  onSection: (section: PersonalSettingsSection) => void;
  onChanged: (message: string) => Promise<void>;
  initialPreferences?: UserPreferences;
  onPreferencesChanged?: (preferences: UserPreferences) => void;
  onPasswordChanged?: (token: TokenResponse) => void;
}) {
  const { t } = useTranslation();
  return <div className="personal-settings-page">
    <PageHead title={t("personal.title")} meta={t("personal.meta")} />
    <nav className="section-nav" aria-label={t("personal.menu")}>
      <button type="button" className={section === "general" || section === "password" ? "on" : ""} onClick={() => onSection("general")}>{t("personal.general")}</button>
      <button type="button" className={section === "notifications" ? "on" : ""} onClick={() => onSection("notifications")}>{t("personal.notifications")}</button>
      <button type="button" className={section === "theme" ? "on" : ""} onClick={() => onSection("theme")}>{t("personal.theme")}</button>
    </nav>
    {section === "general" && <GeneralSettings initialProfile={initialProfile} initialPreferences={initialPreferences} email={email} onPassword={() => onSection("password")} onPreferencesChanged={onPreferencesChanged} onChanged={onChanged} />}
    {section === "notifications" && <NotificationSettings />}
    {section === "theme" && <ThemeSettings initialPreferences={initialPreferences} onPreferencesChanged={onPreferencesChanged} onChanged={onChanged} />}
    {section === "password" && <PasswordSettings onBack={() => onSection("general")} onChanged={onPasswordChanged} onMessage={onChanged} />}
  </div>;
}

function GeneralSettings({ initialProfile, initialPreferences, email, onPassword, onPreferencesChanged, onChanged }: {
  initialProfile?: User; initialPreferences?: UserPreferences; email: string; onPassword: () => void;
  onPreferencesChanged?: (preferences: UserPreferences) => void; onChanged: (message: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<User | undefined>(initialProfile);
  const [displayName, setDisplayName] = useState(initialProfile?.displayName || "");
  const [preferences, setPreferences] = useState<UserPreferences | undefined>(initialPreferences);
  const [locale, setLocale] = useState<UserPreferences["locale"]>(initialPreferences?.locale || "ko");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (initialProfile) { setProfile(initialProfile); setDisplayName(initialProfile.displayName); return; }
    void api.myProfile().then((value) => { setProfile(value); setDisplayName(value.displayName); }).catch((reason) => {
      setError(reason instanceof Error ? reason.message : "개인 정보를 불러오지 못했습니다.");
    });
  }, [initialProfile]);

  useEffect(() => {
    if (initialPreferences) { setPreferences(initialPreferences); setLocale(initialPreferences.locale); return; }
    void api.myPreferences().then((value) => { setPreferences(value); setLocale(value.locale); }).catch(() => undefined);
  }, [initialPreferences]);

  const [attempted, setAttempted] = useState(false);
  async function saveGeneral() {
    const normalized = displayName.trim();
    if (!normalized || !profile) return;
    const nameChanged = normalized !== profile.displayName;
    const localeChanged = Boolean(preferences && locale !== preferences.locale);
    setBusy(true); setError("");
    try {
      if (nameChanged) {
        const saved = await api.updateMyProfile(normalized);
        setProfile(saved); setDisplayName(saved.displayName);
      }
      if (preferences && localeChanged) {
        const saved = await api.updateMyPreferences({ ...preferences, locale });
        setPreferences(saved); applyPreferences(saved); onPreferencesChanged?.(saved);
      }
      await onChanged(localeChanged ? (locale === "en" ? "Settings saved." : "개인 설정을 저장했습니다.") : "이름을 변경했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "이름을 변경하지 못했습니다.");
    } finally { setBusy(false); }
  }

  async function uploadAvatar(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) { setError("PNG 또는 JPEG 이미지를 선택해 주세요."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("이미지는 10MB 이하만 업로드할 수 있습니다."); return; }
    setBusy(true); setError("");
    try {
      setProfile(await api.uploadMyAvatar(file));
      await onChanged("프로필 이미지를 변경했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "프로필 이미지를 변경하지 못했습니다.");
    } finally { setBusy(false); }
  }

  async function removeAvatar() {
    setBusy(true); setError("");
    try {
      setProfile(await api.removeMyAvatar());
      await onChanged("프로필 이미지를 삭제했습니다.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "프로필 이미지를 삭제하지 못했습니다.");
    } finally { setBusy(false); }
  }

  const changed = Boolean(profile && (displayName.trim() !== profile.displayName || (preferences && locale !== preferences.locale)));
  if (!profile && !error) return <Loading layout="form" label={t("common.loadingProfile")} />;
  return <>
    {error && <div className="global-error personal-settings-error">{error}<button onClick={() => setError("")}>닫기</button></div>}
    {profile && <section className="section-card personal-profile-card">
      <header><div><span className="mono-label">PROFILE</span><h2>{t("personal.profile")}</h2><p>{t("personal.profileHelp")}</p></div></header>
      <div className="profile-avatar-setting">
        <Avatar name={profile.displayName} userId={profile.id} avatarVersion={profile.avatarVersion} size="lg" />
        <span><b>{t("personal.avatar")}</b><small>{t("personal.avatarHelp")}</small></span>
        <div>
          <input ref={fileInput} type="file" accept="image/png,image/jpeg" hidden onChange={(event) => void uploadAvatar(event)} />
          <button type="button" disabled={busy} onClick={() => fileInput.current?.click()}><Upload size={14} /> {profile.avatarVersion ? t("personal.change") : t("personal.upload")}</button>
          {profile.avatarVersion && <button type="button" className="profile-avatar-remove" disabled={busy} onClick={() => void removeAvatar()}><Trash2 size={14} /> {t("personal.delete")}</button>}
        </div>
      </div>
      <div className="personal-profile-fields">
        <label><span>{t("personal.name")} <em className="required-mark">{t("common.required")}</em></span><input aria-label={t("personal.name")} aria-invalid={attempted && !displayName.trim()} value={displayName} maxLength={120} disabled={busy} onChange={(event) => setDisplayName(event.target.value)} /></label>
        <label><span>{t("personal.email")}</span><input aria-label={t("personal.email")} value={email} disabled /><small>{t("personal.emailHelp")}</small></label>
        <label><span>{t("personal.language")}</span><select aria-label={t("personal.language")} value={locale} disabled={busy || !preferences} onChange={(event) => setLocale(event.target.value as UserPreferences["locale"])}><option value="ko">{t("personal.korean")}</option><option value="en">{t("personal.english")}</option></select><small>{t("personal.languageHelp")}</small></label>
        <div className="password-setting-row"><span><b>{t("personal.password")}</b><small>{t("personal.passwordHelp")}</small></span><button type="button" onClick={onPassword}><KeyRound size={14} /> {t("personal.changePassword")}</button></div>
      </div>
      {attempted && <RequirementHint missing={displayName.trim() ? [] : [t("personal.name")]} label={t("common.requiredRemaining")} />}
      <footer>
        <button type="button" disabled={busy || !changed} onClick={() => { setDisplayName(profile.displayName); setLocale(preferences?.locale || "ko"); }}>{t("personal.reset")}</button>
        <button type="button" className="primary" disabled={busy || !changed} onClick={event => { setAttempted(true); if (!displayName.trim()) { focusInvalidField(event.currentTarget.closest("section")!); return; } void saveGeneral(); }}>{busy ? t("personal.saving") : t("personal.save")}</button>
      </footer>
    </section>}
  </>;
}

function ThemeSettings({ initialPreferences, onPreferencesChanged, onChanged }: { initialPreferences?: UserPreferences; onPreferencesChanged?: (preferences: UserPreferences) => void; onChanged: (message: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<UserPreferences | undefined>(initialPreferences);
  const [theme, setTheme] = useState<UserPreferences["theme"]>(initialPreferences?.theme || "LIGHT");
  const [kanbanPattern, setKanbanPattern] = useState<KanbanPattern>(initialPreferences?.kanbanPattern || "NONE");
  const [kanbanColor, setKanbanColor] = useState<KanbanColor>(initialPreferences?.kanbanColor || "SLATE");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (initialPreferences) {
      setPreferences(initialPreferences); setTheme(initialPreferences.theme);
      setKanbanPattern(initialPreferences.kanbanPattern || "NONE"); setKanbanColor(initialPreferences.kanbanColor || "SLATE"); return;
    }
    void api.myPreferences().then((value) => {
      setPreferences(value); setTheme(value.theme); setKanbanPattern(value.kanbanPattern || "NONE"); setKanbanColor(value.kanbanColor || "SLATE");
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "Failed to load theme."));
  }, [initialPreferences]);
  async function save() {
    if (!preferences) return;
    setBusy(true); setError("");
    try {
      const saved = await api.updateMyPreferences({ ...preferences, theme, kanbanPattern, kanbanColor });
      setPreferences(saved); applyPreferences(saved); onPreferencesChanged?.(saved);
      await onChanged(t("theme.saved"));
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Failed to save theme."); }
    finally { setBusy(false); }
  }
  const changed = Boolean(preferences && (
    theme !== preferences.theme || kanbanPattern !== (preferences.kanbanPattern || "NONE") || kanbanColor !== (preferences.kanbanColor || "SLATE")
  ));
  const reset = () => {
    setTheme(preferences?.theme || "LIGHT");
    setKanbanPattern(preferences?.kanbanPattern || "NONE");
    setKanbanColor(preferences?.kanbanColor || "SLATE");
  };
  if (!preferences && !error) return <Loading layout="form" />;
  return <>{error && <div className="global-error personal-settings-error">{error}<button onClick={() => setError("")}>{t("common.close")}</button></div>}
    <section className="section-card personal-theme-card"><header><div><span className="mono-label">APPEARANCE</span><h2>{t("theme.title")}</h2><p>{t("theme.help")}</p></div></header>
      <div className="theme-options" role="radiogroup" aria-label={t("theme.title")}>
        <button type="button" role="radio" aria-checked={theme === "LIGHT"} className={theme === "LIGHT" ? "selected" : ""} onClick={() => setTheme("LIGHT")}><span className="theme-preview light"><Sun size={22} /></span><b>{t("theme.light")}</b><small>{t("theme.lightHelp")}</small></button>
        <button type="button" role="radio" aria-checked={theme === "DARK"} className={theme === "DARK" ? "selected" : ""} onClick={() => setTheme("DARK")}><span className="theme-preview dark"><Moon size={22} /></span><b>{t("theme.dark")}</b><small>{t("theme.darkHelp")}</small></button>
      </div>
      <div className="kanban-style-settings">
        <header><div><h3>{t("theme.kanbanTitle")}</h3><p>{t("theme.kanbanHelp")}</p></div><span className="kanban-card-demo"><i className={kanbanStyleClass(kanbanPattern, kanbanColor)} /><b>{t("theme.kanbanPreview")}</b><small>{t("theme.kanbanPreviewMeta")}</small></span></header>
        <fieldset><legend>{t("theme.pattern")}</legend><div className="kanban-pattern-options" role="radiogroup" aria-label={t("theme.pattern")}>
          {kanbanPatterns.map((pattern) => <button key={pattern} type="button" role="radio" aria-checked={kanbanPattern === pattern} aria-label={t(`theme.pattern.${pattern}`)} className={kanbanPattern === pattern ? "selected" : ""} onClick={() => setKanbanPattern(pattern)}><i className={kanbanStyleClass(pattern, kanbanColor)} /><span>{t(`theme.pattern.${pattern}`)}</span></button>)}
        </div></fieldset>
        <fieldset><legend>{t("theme.color")}</legend><div className="kanban-color-options" role="radiogroup" aria-label={t("theme.color")}>
          {kanbanColors.map((color) => <button key={color} type="button" role="radio" aria-checked={kanbanColor === color} aria-label={t(`theme.color.${color}`)} title={t(`theme.color.${color}`)} className={`${kanbanStyleClass("NONE", color)} ${kanbanColor === color ? "selected" : ""}`} onClick={() => setKanbanColor(color)} />)}
        </div></fieldset>
      </div>
      <footer><button type="button" disabled={busy || !changed} onClick={reset}>{t("personal.reset")}</button><button type="button" className="primary" disabled={busy || !changed} onClick={() => void save()}>{busy ? t("personal.saving") : t("personal.save")}</button></footer>
    </section></>;
}

function PasswordSettings({ onBack, onChanged, onMessage }: { onBack: () => void; onChanged?: (token: TokenResponse) => void; onMessage: (message: string) => Promise<void> }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = new FormData(event.currentTarget);
    const current = String(form.get("currentPassword")); const next = String(form.get("newPassword")); const confirm = String(form.get("confirmPassword"));
    if (next !== confirm) { setError(t("password.mismatch")); return; }
    if (current === next) { setError(t("password.same")); return; }
    setBusy(true); setError("");
    try { const token = await api.changePassword(current, next); onChanged?.(token); await onMessage(t("password.changed")); onBack(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Failed to change password."); }
    finally { setBusy(false); }
  }
  return <section className="section-card personal-password-card"><header><button type="button" className="password-back" onClick={onBack}><ChevronLeft size={16} /> {t("personal.general")}</button><div><span className="mono-label">SECURITY</span><h2>{t("password.title")}</h2><p>{t("password.meta")}</p></div></header>
    <form onSubmit={submit}><label><span>{t("password.current")}</span><input name="currentPassword" type="password" autoComplete="current-password" required /></label><label><span>{t("password.new")}</span><input name="newPassword" type="password" minLength={10} autoComplete="new-password" required /><small>{t("password.rule")}</small></label><label><span>{t("password.confirm")}</span><input name="confirmPassword" type="password" minLength={10} autoComplete="new-password" required /></label>{error && <div className="error-box">{error}</div>}<footer><button type="button" onClick={onBack}>{t("password.cancel")}</button><button className="primary" disabled={busy}>{busy ? t("password.changing") : t("password.submit")}</button></footer></form>
  </section>;
}

function NotificationSettings() {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState<NotificationPreference | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [pushConfiguration, setPushConfiguration] = useState<WebPushConfiguration | null>(null);
  const [browserSubscribed, setBrowserSubscribed] = useState(false);

  useEffect(() => {
    void api.notificationPreferences().then(setPreferences).catch((reason) => {
      setError(reason instanceof Error ? reason.message : t("notifications.loadError"));
    });
    void api.webPushConfiguration().then(setPushConfiguration).catch(() => undefined);
    void currentWebPushSubscription().then((subscription) => setBrowserSubscribed(Boolean(subscription)));
  }, []);

  async function save(next: NotificationPreference) {
    setBusy(true); setError(""); setSaved(false);
    try {
      setPreferences(await api.updateNotificationPreferences(next));
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("notifications.saveError"));
    } finally { setBusy(false); }
  }

  async function setWebPushEnabled(enabled: boolean) {
    if (!preferences) return;
    setBusy(true); setError(""); setSaved(false);
    try {
      if (enabled) {
        const configuration = pushConfiguration ?? await api.webPushConfiguration();
        setPushConfiguration(configuration);
        const publicKey = resolveWebPushPublicKey(configuration.publicKey);
        if (!configuration.available || !publicKey) {
          throw new Error(t("notifications.pushKeyMissing"));
        }
        const subscription = await subscribeCurrentBrowser(publicKey);
        await api.saveWebPushSubscription(subscription);
        setBrowserSubscribed(true);
      } else {
        const endpoint = await unsubscribeCurrentBrowser();
        if (endpoint) await api.removeWebPushSubscription(endpoint);
        setBrowserSubscribed(false);
      }
      setPreferences(await api.updateNotificationPreferences({ ...preferences, webPushEnabled: enabled }));
      setSaved(true); window.setTimeout(() => setSaved(false), 1800);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("notifications.pushChangeError"));
    } finally { setBusy(false); }
  }

  async function setCurrentBrowserConnected(connected: boolean) {
    if (!preferences) return;
    setBusy(true); setError("");
    try {
      if (connected) {
        const configuration = pushConfiguration ?? await api.webPushConfiguration();
        setPushConfiguration(configuration);
        const publicKey = resolveWebPushPublicKey(configuration.publicKey);
        if (!configuration.available || !publicKey) throw new Error(t("notifications.pushKeyMissing"));
        await api.saveWebPushSubscription(await subscribeCurrentBrowser(publicKey));
        setBrowserSubscribed(true);
        if (!preferences.webPushEnabled) {
          setPreferences(await api.updateNotificationPreferences({ ...preferences, webPushEnabled: true }));
        }
      } else {
        const endpoint = await unsubscribeCurrentBrowser();
        if (endpoint) await api.removeWebPushSubscription(endpoint);
        setBrowserSubscribed(false);
      }
      setSaved(true); window.setTimeout(() => setSaved(false), 1800);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("notifications.browserChangeError"));
    } finally { setBusy(false); }
  }

  if (!preferences && !error) return <Loading layout="form" label={t("notifications.loadError")} />;

  return <div className="notification-settings-page">
    {saved && <div className="personal-settings-saved">{t("common.saved")}</div>}
    {error && <div className="global-error">{error}<button onClick={() => setError("")}>{t("common.close")}</button></div>}
    {preferences && <>
      <section className="section-card notification-channel-card">
        <header><div><span className="mono-label">CHANNELS</span><h2>{t("notifications.channels")}</h2></div></header>
        <div className="preference-list">
          <div className="preference-row">
            <span className="preference-icon"><BellRing size={18} /></span>
            <span><b>{t("notifications.inApp")}</b><small>{t("notifications.inAppHelp")}</small></span>
            <Toggle on={preferences.inAppEnabled} label={t("notifications.inApp")} disabled={busy} onChange={(value) => void save({ ...preferences, inAppEnabled: value })} />
          </div>
          <div className="preference-row">
            <span className="preference-icon"><Mail size={18} /></span>
            <span><b>{t("notifications.email")}</b><small>{t("notifications.emailHelp")}</small></span>
            <Toggle on={preferences.emailEnabled ?? false} label={t("notifications.email")} disabled={busy} onChange={(value) => void save({ ...preferences, emailEnabled: value })} />
          </div>
          <div className="preference-row web-push-row">
            <span className="preference-icon"><MonitorSmartphone size={18} /></span>
            <span><b>{t("notifications.webPush")}</b><small>{!supportsWebPush()
              ? t("notifications.unsupported")
              : pushConfiguration?.available === false
                ? t("notifications.serverSetup")
                : browserSubscribed
                  ? t("notifications.browserConnected")
                  : t("notifications.permissionHint")}</small>
              {preferences.webPushEnabled && supportsWebPush() && pushConfiguration?.available && <button
                type="button" className="text-action push-device-action" disabled={busy}
                onClick={() => void setCurrentBrowserConnected(!browserSubscribed)}
              >{t(browserSubscribed ? "notifications.disconnectBrowser" : "notifications.connectBrowser")}</button>}</span>
            <Toggle
              on={preferences.webPushEnabled}
              label={t("notifications.webPush")}
              disabled={busy || !supportsWebPush() || pushConfiguration?.available === false}
              onChange={(value) => void setWebPushEnabled(value)}
            />
          </div>
        </div>
      </section>

      <section className="section-card notification-kind-card">
        <header><div><span className="mono-label">EVENTS</span><h2>{t("notifications.events")}</h2></div></header>
        <p className="permission-note">{t("notifications.eventHelp")}</p>
        <div className="preference-list">
          {notificationKinds.map((kind) => <div className="preference-row" key={kind.key}>
            <span><b>{t(kind.titleKey)}</b><small>{t(kind.descriptionKey)}</small></span>
            <Toggle
              on={preferences[kind.key]}
              label={t(kind.titleKey)}
              disabled={busy}
              onChange={(value) => void save({ ...preferences, [kind.key]: value })}
            />
          </div>)}
        </div>
      </section>
    </>}
  </div>;
}
